import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import API_URL from '../config/api';
import { apiFetch } from '../utils/apiFetch';

const PaymentCheckout: React.FC = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const invoiceId = searchParams.get('invoiceId');
  const amount = searchParams.get('amount');
  
  const [loading, setLoading] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [name, setName] = useState('');

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!token || !invoiceId || !amount) {
      toast.error('Invalid payment request');
      return;
    }

    setLoading(true);

    try {
      const response = await apiFetch(`${API_URL}/payments/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          invoiceId,
          amount: parseFloat(amount),
          paymentMethod: {
            card: {
              number: cardNumber.replace(/\s/g, ''),
              exp_month: parseInt(expiry.split('/')[0]),
              exp_year: parseInt('20' + expiry.split('/')[1]),
              cvc
            },
            billing_details: {
              name
            }
          }
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('✅ Payment successful!');
        navigate(`/invoices/${invoiceId}`);
      } else {
        toast.error(data.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Network error - please try again');
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  const formatExpiry = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  return (
    <div style={{ maxWidth: 500, margin: '40px auto', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 30, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <h2 style={{ marginTop: 0 }}>💳 Payment Checkout</h2>
        
        <div style={{ background: '#f5f5f5', padding: 15, borderRadius: 5, marginBottom: 20 }}>
          <p style={{ margin: 0, fontSize: 14, color: '#666' }}>Amount to pay:</p>
          <p style={{ margin: '5px 0 0 0', fontSize: 32, fontWeight: 'bold', color: '#333' }}>
            £{parseFloat(amount || '0').toFixed(2)}
          </p>
        </div>

        <form onSubmit={handlePayment}>
          <div style={{ marginBottom: 15 }}>
            <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>
              Cardholder Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Smith"
              required
              style={{
                width: '100%',
                padding: 12,
                border: '1px solid #ddd',
                borderRadius: 5,
                fontSize: 16,
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 15 }}>
            <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>
              Card Number *
            </label>
            <input
              type="text"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              placeholder="4242 4242 4242 4242"
              maxLength={19}
              required
              style={{
                width: '100%',
                padding: 12,
                border: '1px solid #ddd',
                borderRadius: 5,
                fontSize: 16,
                boxSizing: 'border-box',
                fontFamily: 'monospace'
              }}
            />
            <p style={{ margin: '5px 0 0 0', fontSize: 12, color: '#666' }}>
              Test card: 4242 4242 4242 4242
            </p>
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>
                Expiry (MM/YY) *
              </label>
              <input
                type="text"
                value={expiry}
                onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                placeholder="12/25"
                maxLength={5}
                required
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #ddd',
                  borderRadius: 5,
                  fontSize: 16,
                  boxSizing: 'border-box',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500 }}>
                CVC *
              </label>
              <input
                type="text"
                value={cvc}
                onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').substring(0, 4))}
                placeholder="123"
                maxLength={4}
                required
                style={{
                  width: '100%',
                  padding: 12,
                  border: '1px solid #ddd',
                  borderRadius: 5,
                  fontSize: 16,
                  boxSizing: 'border-box',
                  fontFamily: 'monospace'
                }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={{
                flex: 1,
                padding: 14,
                background: '#f5f5f5',
                border: 'none',
                borderRadius: 5,
                fontSize: 16,
                fontWeight: 500,
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 2,
                padding: 14,
                background: loading ? '#ccc' : '#4CAF50',
                color: '#fff',
                border: 'none',
                borderRadius: 5,
                fontSize: 16,
                fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Processing...' : `Pay £${parseFloat(amount || '0').toFixed(2)}`}
            </button>
          </div>
        </form>

        <div style={{ marginTop: 20, padding: 15, background: '#f9f9f9', borderRadius: 5, fontSize: 12, color: '#666' }}>
          <p style={{ margin: 0, marginBottom: 5 }}>🔒 Secure payment powered by Stripe</p>
          <p style={{ margin: 0 }}>Your payment information is encrypted and secure</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentCheckout;
